import { useAudioPlayerContext } from './context/AudioPlayerContext';
import { useEffect, useRef } from 'react';
import Speedometer from '../../assets/icons/audioplayer/speedometer.svg';
import Tick from '../../assets/icons/audioplayer/check.svg';
import { motion, AnimatePresence } from 'framer-motion';

const CustomDropdown = ({
    options,
    selectedOption,
    onSelect,
    closeDropdown,
}: {
    options: number[];
    selectedOption: number;
    onSelect: (option: number) => void;
    closeDropdown: () => void;
}) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 70 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 70 }}
            transition={{
                duration: 0.2,
                type: '',
                stiffness: 500, // Lower stiffness for slower movement
                damping: 2,    // Higher damping for smoother end
            }}
            className="flex flex-col items-center w-40 gap-2 absolute right-[7px] bottom-[60px] bg-secondary-700 py-4 border border-secondary-300 rounded-sm z-10 text-base"
            onClick={(e) => e.stopPropagation()} // Prevent click inside dropdown from closing it
        >
            {options.map((option) => (
                <button
                    key={option}
                    onClick={() => {
                        onSelect(option);
                        closeDropdown();
                    }}
                    className={`flex items-center justify-between w-full px-3 py-1 hover:bg-secondary-400 ${selectedOption === option
                            ? 'font-bold bg-secondary-500 rounded'
                            : ''
                        }`}
                >
                    {option} x
                    {selectedOption === option && (
                        <img src={Tick} srcSet={`${Tick} 1x, ${Tick} 2x`} loading="lazy" alt="Tick" className="w-4 h-4" />
                    )}
                </button>
            ))}
        </motion.div>
    );
};

export const SpeedControl = ({ toggleSpeedMenu, speedMenuOpen }: { toggleSpeedMenu: () => void, speedMenuOpen: boolean }) => {
    const { playbackSpeed, setPlaybackSpeed } = useAudioPlayerContext();
    const { audioRef } = useAudioPlayerContext();
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleSpeedChange = (speed: number) => {
        setPlaybackSpeed(speed);
    };

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.playbackRate = playbackSpeed;
        }
    }, [playbackSpeed, audioRef]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                toggleSpeedMenu();
            }
        };

        if (speedMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [speedMenuOpen]);

    const speedOptions = [0.25, 0.5, 1, 1.5, 2];

    return (
        <section className="flex flex-col relative text-white items-center gap-3 font-kanit" ref={dropdownRef}>
            <AnimatePresence>
                {speedMenuOpen && (
                    <CustomDropdown
                        options={speedOptions}
                        selectedOption={playbackSpeed}
                        onSelect={handleSpeedChange}
                        closeDropdown={toggleSpeedMenu}
                    />
                )}
            </AnimatePresence>
            <div className="flex font-kanit">
                <button onClick={toggleSpeedMenu} title="Playback Speed">
                    <img src={Speedometer} srcSet={`${Speedometer} 1x, ${Speedometer} 2x`} loading="lazy" alt="Speedometer" className="w-5 h-5" />
                </button>
            </div>
        </section>
    );
};
